/**
 * Satellite Insights: the farmer app's satellite logic, for the Support page.
 *
 * Every function below is copied verbatim from farmer-app (the source file is
 * named above each section) so a field reads exactly as it does for the
 * farmer: the same CropGen class tones and cut-offs, signals, verdict, patch
 * letters and advice, findings order and season-by-season maths. Only what the
 * three Support sections use is here. Change the farmer app first, then this.
 *
 * Pure functions, no React. Texts are { key, params } (see satelliteText.js).
 */
import area from '@turf/area';
import { polygon as turfPolygon } from '@turf/helpers';

/* -------- rules (farmer-app src/config/satelliteRules.js) -------- */

/**
 * CropGen's classes per index, as captured from live answers on 2026-09-24.
 *
 * `kind` separates the two ways water can be wrong. A dry patch and a
 * waterlogged patch both need a look, and they need opposite advice.
 */
export const CLASS_TONES = {
  ndmi: {
    'very dry': { tone: 'bad', kind: 'dry' },
    dry: { tone: 'bad', kind: 'dry' },
    'low moisture': { tone: 'watch', kind: 'dry' },
    'moderate moisture': { tone: 'good' },
    moist: { tone: 'good' },
    'good moisture': { tone: 'good' },
    'high moisture': { tone: 'good' },
    'very moist': { tone: 'good' },
    wet: { tone: 'watch', kind: 'wet' },
    waterlogged: { tone: 'watch', kind: 'wet' },
  },
  /**
   * NDWI, as captured from a live answer on 2026-10-24. Toned the way NDMI is:
   * a dry class needs action, a low one is worth watching, a waterlogged one
   * is worth a look for the opposite reason. This mirrors the NDMI rule by the
   * product owner's decision, pending an agronomist's own NDWI table.
   */
  ndwi: {
    'very dry': { tone: 'bad', kind: 'dry' },
    dry: { tone: 'bad', kind: 'dry' },
    'low water': { tone: 'watch', kind: 'dry' },
    'moderate water': { tone: 'good' },
    moist: { tone: 'good' },
    'high moisture': { tone: 'good' },
    'very moist': { tone: 'good' },
    wet: { tone: 'watch', kind: 'wet' },
    'water saturated': { tone: 'watch', kind: 'wet' },
    waterlogged: { tone: 'watch', kind: 'wet' },
  },
  ndvi: {
    'very poor': { tone: 'bad' },
    poor: { tone: 'bad' },
    fair: { tone: 'watch' },
    moderate: { tone: 'good' },
    good: { tone: 'good' },
    'very good': { tone: 'good' },
    excellent: { tone: 'good' },
    dense: { tone: 'good' },
    'very dense': { tone: 'good' },
    extreme: { tone: 'good' },
  },
  ndre: {
    'very low': { tone: 'bad' },
    low: { tone: 'bad' },
    fair: { tone: 'watch' },
    moderate: { tone: 'good' },
    good: { tone: 'good' },
    'very good': { tone: 'good' },
    excellent: { tone: 'good' },
    dense: { tone: 'good' },
    'very dense': { tone: 'good' },
    extreme: { tone: 'good' },
  },
};

/** Classes that mean "the satellite could not see", in every index. */
export const CLOUD_LABELS = ['clouds', 'cloud'];

/**
 * When the field as a whole is flagged. Every share is a percentage of the
 * CLEAR part of the field — cloud is neither good nor bad, it is unseen.
 */
export const RATING_CUTOFFS = {
  /** At or above this much cloud, nothing is rated: "too cloudy to read". */
  cloudyPct: 60,
  /** At or above this much in bad classes: NEEDS ACTION. */
  badPct: 25,
  /** At or above this much in bad classes: WATCH. */
  watchBadPct: 10,
  /** At or above this much in watch classes: WATCH. */
  watchPct: 30,
  /** Above this much in classes we do not recognise: unknown, not rated. */
  unknownPct: 20,
};

/**
 * Reading the class-map picture, to find WHERE the patches are.
 *
 * CropGen shades the picture: a pixel is its legend colour dimmed to roughly
 * 0.82 on every channel, varying pixel to pixel (see
 * src/services/__tests__/fixtures/README.md). So pixels are matched by colour
 * direction, allowing any brightness in `scaleRange`, not by exact colour.
 */
export const IMAGE_MATCH = {
  /** How much a pixel may be dimmed or brightened relative to its class colour. */
  scaleRange: [0.6, 1.15],
  /** Largest RGB distance, after the best scale, still accepted as a match. */
  maxResidual: 24,
  /** Read every Nth pixel in each direction; 800 px / 4 = 200 × 200 samples. */
  sampleStep: 4,
  /** Pixels more transparent than this are outside the field. */
  alphaMin: 128,
  /**
   * The pixel shares must agree with the legend's own percentages to within
   * this many points in every class, or the patch locations are not shown.
   */
  legendAgreementPts: 5,
  /** A sector must hold at least this share of the field's pixels to be a patch. */
  minSectorSharePct: 5,
  /** At most this many patches are named. */
  maxPatches: 3,
};

/**
 * "Season by season": the same stretch of the year, looked at year after year.
 *
 * Each season is one CropGen timeseries of NDVI over `windowDays`, ending today
 * (the current season) or on today's date k years ago. Comparing like months
 * with like months is the point — a field in March and the same field in
 * August are different crops, not a trend.
 */
export const SEASON_HISTORY = {
  /** How many past seasons, besides the current one. */
  years: 5,
  /** The length of each season's window. The current one is the 90-day trend already fetched. */
  windowDays: 90,
  /** The calendar the backend counts days in. */
  timeZone: 'Asia/Kolkata',
  /** A change in mean NDVI smaller than this reads as "steady". */
  flatDelta: 0.03,
  /** A season with fewer readings than this is shown as missing, not scored. */
  minReadings: 2,
};

/* -------- index answers (farmer-app src/services/ndviModel.js) -------- */

export const NDVI_STATUS = {
  OK: 'OK',
  NO_SCENE: 'NO_SCENE',
  NO_VALID_OBSERVATION: 'NO_VALID_OBSERVATION',
  UNAVAILABLE: 'UNAVAILABLE',
};

export const NDVI_QUALITY = {
  EXCELLENT: 'EXCELLENT',
  GOOD: 'GOOD',
  PARTIAL: 'PARTIAL',
  POOR: 'POOR',
  REJECTED: 'REJECTED',
  NO_VALID_OBSERVATION: 'NO_VALID_OBSERVATION',
};

/** `{north, south, east, west}` -> the `[[s, w], [n, e]]` Leaflet wants. */
export const boundsToLeaflet = (bounds) => {
  if (!bounds) return null;
  const { north, south, east, west } = bounds;
  if (![north, south, east, west].every((n) => Number.isFinite(n))) return null;
  return [
    [south, west],
    [north, east],
  ];
};

/**
 * The magic bytes each image format starts with, as they appear once base64
 * has encoded them.
 *
 * Base64 maps three bytes onto four characters, so a fixed-length file header
 * always produces the same leading characters — `iVBORw0KGgo` is just PNG's
 * `\x89PNG\r\n\x1a\n` seen through that encoding.
 */
const BASE64_IMAGE_SIGNATURES = [
  ['iVBORw0KGgo', 'image/png'],
  ['/9j/', 'image/jpeg'],
  ['R0lGOD', 'image/gif'],
  ['UklGR', 'image/webp'],
];

/**
 * Turns the provider's encoded image into something an `<img>` can load.
 *
 * The format is read out of the bytes rather than assumed. Guessing `image/png`
 * would work right up until the provider sent a JPEG, and the failure would be
 * a blank overlay with nothing in the console to explain it — the browser
 * simply declines to decode a data URL whose declared type is wrong.
 *
 * An already-complete data URL is passed straight through, so a provider that
 * starts sending one needs no change here. Anything unrecognised returns null,
 * and the caller treats that exactly as it treats a missing image: no overlay,
 * and the state the farmer already understands. A broken `<img>` over their
 * field would be worse than none.
 *
 * Nothing is decoded or re-encoded. The bytes handed over are the bytes that
 * arrived; the browser does the rest.
 */
export const imageSourceFrom = (imageBase64) => {
  if (typeof imageBase64 !== 'string') return null;

  const value = imageBase64.trim();
  if (value.length === 0) return null;
  if (value.startsWith('data:')) return value;

  const match = BASE64_IMAGE_SIGNATURES.find(([prefix]) => value.startsWith(prefix));
  if (!match) return null;

  return `data:${match[1]};base64,${value}`;
};

/**
 * The legend entries that can actually be drawn.
 *
 * The provider decides both the classes and their colours — they differ by
 * index, and a moisture scale has neither the same bands nor the same number of
 * them as a vigour scale. So nothing here supplies a colour, a label, an order
 * or a threshold; this only drops entries too incomplete to render, which would
 * otherwise appear as a blank chip that looks like a real class.
 */
export const legendSwatches = (legend) => {
  if (!Array.isArray(legend)) return [];
  return legend.filter(
    (entry) =>
      entry &&
      typeof entry.color === 'string' &&
      entry.color.length > 0 &&
      typeof entry.label === 'string' &&
      entry.label.length > 0
  );
};

const asNumber = (value) => (Number.isFinite(Number(value)) ? Number(value) : null);

/**
 * Normalises whatever the endpoint returned into one predictable shape.
 *
 * A null payload — which farmerApi returns when the endpoint is not deployed —
 * becomes UNAVAILABLE rather than throwing, the way every not-yet-deployed
 * endpoint degrades.
 *
 * The statistics arrive under a key named after the index — `ndvi`, `ndmi`,
 * `ndre` — which the payload also states in `index`. Reading the name from the
 * payload rather than from the caller means a response can never be normalised
 * as the wrong index; the fallback to 'ndvi' covers a backend that predates the
 * `index` field, which could only ever have been sending NDVI.
 */
export const normaliseIndex = (raw) => {
  if (!raw) return { status: NDVI_STATUS.UNAVAILABLE, hasMeasurement: false };

  const status = raw.status || NDVI_STATUS.UNAVAILABLE;
  const index = raw.index || 'ndvi';

  if (status !== NDVI_STATUS.OK) {
    return {
      status,
      index,
      quality: raw.quality || NDVI_QUALITY.NO_VALID_OBSERVATION,
      hasMeasurement: false,
      searchedFrom: raw.searched_from || null,
      searchedTo: raw.searched_to || null,
      message: raw.message || null,
    };
  }

  const stats = raw[index] || {};
  const grid = raw.grid || null;

  return {
    status: NDVI_STATUS.OK,
    index,
    hasMeasurement: true,
    quality: raw.quality || null,
    /**
     * Never defaulted. This used to fall back to 'Sentinel-2', which was true
     * of the only provider there had ever been and became a claim the payload
     * no longer supports the moment that changed. An unnamed sensor is a gap;
     * a wrongly named one is a lie the screen tells confidently.
     */
    satellite: raw.satellite || null,
    date: raw.date || null,
    source: raw.source || null,
    collection: raw.collection || null,
    algorithmVersion: raw.algorithm_version || null,
    sceneIds: Array.isArray(raw.scene_ids) ? raw.scene_ids : [],
    acquiredAt: raw.acquisition_date || null,
    /** Provenance only — never shown as the farm's cloud cover. */
    sceneCloudCover: asNumber(raw.scene_cloud_cover),
    validPixelPercentage: asNumber(raw.farm_valid_pixel_percentage),
    invalidPixelPercentage: asNumber(raw.invalid_pixel_percentage),
    validPixelCount: asNumber(raw.valid_pixel_count),
    totalPixelCount: asNumber(raw.total_pixel_count),
    stats: {
      mean: asNumber(stats.mean),
      median: asNumber(stats.median),
      min: asNumber(stats.min),
      max: asNumber(stats.max),
      std: asNumber(stats.std),
      p10: asNumber(stats.p10),
      p25: asNumber(stats.p25),
      p75: asNumber(stats.p75),
      p90: asNumber(stats.p90),
      cv: asNumber(stats.coefficient_of_variation),
      resolutionM: asNumber(stats.resolution_m),
    },
    grid:
      grid && grid.width && grid.height && Array.isArray(grid.values)
        ? {
            width: grid.width,
            height: grid.height,
            values: grid.values,
            bounds: grid.bounds || raw.bounds || null,
            /** The display cell size, which is not the sensor's resolution. */
            approxCellSizeM: grid.approx_cell_size_m || null,
            sourceResolutionM: asNumber(grid.source_resolution_m),
          }
        : null,
    bounds: raw.bounds || (grid && grid.bounds) || null,
    rasterKey: raw.raster ? raw.raster.key : null,

    /**
     * The finished image, and the classes that explain its colours.
     *
     * Both arrive already made. The index was computed, masked and coloured
     * before it reached us, which is why there is no ramp to apply and no
     * threshold to pick: doing either here would mean drawing a second opinion
     * over the first and having no way to tell which the farmer was looking at.
     *
     * `grid` above still exists for the historical raster, which is a different
     * endpoint and still sends numbers. One normaliser serves both, so a
     * response carries whichever of the two its endpoint produces.
     */
    imageBase64: raw.image_base64 || null,
    legend: Array.isArray(raw.legend) ? raw.legend : [],
    /**
     * Carried but not drawn. Nothing yet knows its shape — the only response we
     * have seen puts null here — and inventing a layout for data we have never
     * seen is how a screen ends up asserting something its source never said.
     */
    areaStats: raw.area_stats ?? null,
  };
};

/**
 * Normalises the history payload.
 *
 * Mirrors normaliseIndex: snake_case on the wire, camelCase in the components,
 * and every number passed through asNumber so a string from Postgres cannot
 * reach the chart as one. Each entry keeps all three index blocks, because the
 * index selector switches series from data already in hand.
 */
export const normaliseIndexHistory = (raw) => {
  if (!raw) {
    return {
      status: NDVI_STATUS.UNAVAILABLE,
      index: null,
      entries: [],
      sceneCount: 0,
      summary: null,
    };
  }

  /**
   * One index, and exactly the dates it was measured on.
   *
   * Nothing is filled in to line up with another index. The two are measured on
   * different days, so a date that is absent here is simply a date this index
   * has no reading for — not a gap to be explained, and certainly not one to be
   * manufactured by comparing against a series the farmer is not looking at.
   */
  const entries = (Array.isArray(raw.observations) ? raw.observations : []).map(
    (entry) => ({
      date: entry.date || null,
      mean: asNumber(entry.mean),
      /** The provider's own word for the reading — 'Good', 'Very Poor'. */
      rating: entry.rating || null,
    })
  );

  return {
    status: raw.status || NDVI_STATUS.UNAVAILABLE,
    farmId: raw.farm_id || null,
    index: raw.index || null,
    searchedFrom: raw.searched_from || null,
    searchedTo: raw.searched_to || null,
    entries,
    /** How many dates this index was measured on, which is all of them. */
    sceneCount: entries.length,
    /** The provider's own min/mean/max over the window, as given. */
    summary: raw.summary || null,
    message: raw.message || null,
  };
};

/* -------- legend (farmer-app src/services/satelliteLegend.js) -------- */

/** Worst last, so a larger number is a worse field. */
export const TONE_ORDER = { good: 0, watch: 1, bad: 2 };

export const isTone = (value) => Object.prototype.hasOwnProperty.call(TONE_ORDER, value);

/**
 * The worse of two tones. A value that is not a tone (`cloudy`, `unknown`,
 * undefined) cannot make a field worse, so the other one wins; if neither is a
 * tone the first is returned unchanged.
 */
export const worseOf = (a, b) => {
  if (!isTone(a)) return isTone(b) ? b : a;
  if (!isTone(b)) return a;
  return TONE_ORDER[b] > TONE_ORDER[a] ? b : a;
};

const normaliseLabel = (label) => String(label).trim().toLowerCase().replace(/\s+/g, ' ');

const asPercent = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const round1 = (n) => Math.round(n * 10) / 10;

/**
 * One class of the legend, with the tone our rules give it.
 *
 * `tone` is 'good' | 'watch' | 'bad' | 'cloud' | 'unknown'. `kind` is only set
 * where a tone can mean two opposite things (NDMI: 'dry' or 'wet').
 */
const classifyEntry = (entry, table) => {
  const key = normaliseLabel(entry.label);
  if (CLOUD_LABELS.includes(key)) {
    return { label: entry.label.trim(), color: entry.color, percent: asPercent(entry.percent), tone: 'cloud', kind: null };
  }
  const rule = table[key];
  return {
    label: entry.label.trim(),
    color: entry.color,
    percent: asPercent(entry.percent),
    tone: rule ? rule.tone : 'unknown',
    kind: rule && rule.kind ? rule.kind : null,
  };
};

/**
 * The legend, sorted into tones and summed.
 *
 * Shares under `shares` are percentages of the CLEAR field (cloud removed), so
 * a field half under cloud and fully dry underneath reads 100 % dry, not 50 %.
 * `cloudPct` is a percentage of the whole field.
 *
 * @param {Array} legend CropGen's legend entries
 * @param {'ndvi'|'ndmi'|'ndre'} index which index the legend belongs to
 */
export const classifyLegend = (legend, index) => {
  const table = CLASS_TONES[index] || {};
  const classes = legendSwatches(legend).map((entry) => classifyEntry(entry, table));

  const sum = (predicate) =>
    classes.filter(predicate).reduce((total, c) => total + c.percent, 0);

  const total = sum(() => true);
  const cloud = sum((c) => c.tone === 'cloud');
  const clear = total - cloud;
  const ofClear = (value) => (clear > 0 ? round1((value / clear) * 100) : 0);

  return {
    index,
    classes,
    hasData: classes.length > 0 && total > 0,
    cloudPct: total > 0 ? round1((cloud / total) * 100) : 0,
    clearPct: total > 0 ? round1((clear / total) * 100) : 0,
    shares: {
      good: ofClear(sum((c) => c.tone === 'good')),
      watch: ofClear(sum((c) => c.tone === 'watch')),
      bad: ofClear(sum((c) => c.tone === 'bad')),
    },
    dryPct: ofClear(sum((c) => c.kind === 'dry' && (c.tone === 'bad' || c.tone === 'watch'))),
    wetPct: ofClear(sum((c) => c.kind === 'wet')),
    unknownPct: ofClear(sum((c) => c.tone === 'unknown')),
    unknownLabels: classes.filter((c) => c.tone === 'unknown').map((c) => c.label),
  };
};

/**
 * The field's rating from a classified legend.
 *
 * @returns {'good'|'watch'|'bad'|'cloudy'|'unknown'}
 */
export const rateClassified = (classified) => {
  if (!classified || !classified.hasData) return 'unknown';
  if (classified.cloudPct >= RATING_CUTOFFS.cloudyPct) return 'cloudy';
  if (classified.unknownPct > RATING_CUTOFFS.unknownPct) return 'unknown';

  const { bad, watch } = classified.shares;
  if (bad >= RATING_CUTOFFS.badPct) return 'bad';
  if (bad >= RATING_CUTOFFS.watchBadPct || watch >= RATING_CUTOFFS.watchPct) return 'watch';
  return 'good';
};

/**
 * The tone of one of CropGen's class names, for an index: the same rules the
 * legend is sorted by. The history payload rates each reading in these words
 * ('Moderate', 'Very Poor'), so a past season is toned exactly as today is.
 *
 * @returns {'good'|'watch'|'bad'|'cloud'|'unknown'}
 */
export const toneForRating = (index, label) => {
  if (!label) return 'unknown';
  const key = normaliseLabel(label);
  if (CLOUD_LABELS.includes(key)) return 'cloud';
  const rule = (CLASS_TONES[index] || {})[key];
  return rule ? rule.tone : 'unknown';
};

/** The class with the largest share among those of a given tone, or null. */
export const topClassOf = (classified, tone) => {
  if (!classified) return null;
  const matching = classified.classes.filter((c) => c.tone === tone && c.percent > 0);
  if (!matching.length) return null;
  return matching.reduce((best, c) => (c.percent > best.percent ? c : best));
};

/* -------- signals (farmer-app src/services/satelliteSignals.js) -------- */

const text = (key, params = {}) => ({ key, params });
const parts = (list) => ({ parts: list.filter(Boolean) });

/** The pill label for a tone. `good`, `watch` and `needsAction` already exist. */
export const statusKeyFor = (tone) =>
  ({
    good: 'good',
    watch: 'watch',
    bad: 'needsAction',
    cloudy: 'satCloudy',
  }[tone] || 'satUnknown');

const pct = (n) => Math.round(Number(n) || 0);

const flaggedPatches = (patches) =>
  (Array.isArray(patches) ? patches : []).filter((p) => p.tone === 'bad' || p.tone === 'watch');

/** The "nothing to read" texts both signals share. */
const unreadable = (tone, date) =>
  tone === 'cloudy' ? text('sigTooCloudy', { date }) : text('sigNoReading');

/* -------------------------------------------------------------- water ---- */

/**
 * Water Stress, from NDMI.
 *
 * @param {object} args
 * @param {object} args.classified classifyLegend of the NDMI answer
 * @param {string} [args.date] the NDMI picture's date
 * @param {Array}  [args.patches] sectorPatches of the NDMI picture
 */
export const buildWaterSignal = ({ classified, date = null, patches = [] } = {}) => {
  const tone = rateClassified(classified);
  const base = {
    id: 'water',
    icon: 'water',
    titleKey: 'sigWaterTitle',
    tone,
    statusKey: statusKeyFor(tone),
    date,
    source: text('sigWaterSource', { date }),
  };

  if (!isTone(tone)) {
    return { ...base, plain: unreadable(tone, date), detail: null, todo: null, patches: [] };
  }

  const flagged = flaggedPatches(patches);
  const { dryPct, wetPct } = classified;
  const kind = wetPct > dryPct ? 'wet' : 'dry';
  const lead = flagged[0];
  const placeKey = lead ? lead.placeKey : 'placeWholeField';

  // A field can be fine overall with one corner that is not: the pill says
  // GOOD, so the words must not read as an alarm — they name the corner gently.
  let plain;
  if (tone === 'good') {
    plain = lead ? text('waterGoodExcept', { placeKey }) : text('waterEven');
  } else if (flagged.length) {
    plain = parts(
      flagged
        .slice(0, 2)
        .map((p) => text(p.kind === 'wet' ? 'waterPlaceWet' : 'waterPlaceDry', { placeKey: p.placeKey }))
    );
  } else {
    plain = kind === 'wet' ? text('waterWetShare', { pct: pct(wetPct) }) : text('waterDryShare', { pct: pct(dryPct) });
  }

  const top = topClassOf(classified, tone === 'good' ? 'good' : tone);
  // Name only the side that is there: "100% dry and 0% too wet" is noise.
  let detail;
  if (tone === 'good') detail = text('waterDetailGood', { pct: pct(classified.shares.good), topClass: top ? top.label : '' });
  else if (pct(wetPct) === 0) detail = text('waterDetailDry', { dryPct: pct(dryPct) });
  else if (pct(dryPct) === 0) detail = text('waterDetailWet', { wetPct: pct(wetPct) });
  else detail = text('waterDetailShares', { dryPct: pct(dryPct), wetPct: pct(wetPct) });

  let todo;
  if (tone === 'good') todo = lead ? text('todoWaterGoodLook', { placeKey }) : text('todoWaterGood');
  else if ((lead ? lead.kind : kind) === 'wet') todo = text('todoWaterWet', { placeKey });
  else if (tone === 'bad') todo = text('todoWaterDry', { placeKey });
  else todo = text('todoWaterWatchDry', { placeKey });

  return { ...base, plain, detail, todo, patches: flagged };
};

/* ------------------------------------------------------------- growth ---- */

/**
 * Crop Growth, from NDVI, with NDRE as a second opinion.
 *
 * NDVI decides. NDRE (leaf colour) can raise the card to WATCH — pale leaves
 * are worth a look — but never on its own to NEEDS ACTION, and it cannot rate
 * a field NDVI could not see.
 *
 * @param {object} args
 * @param {object} args.ndvi classifyLegend of the NDVI answer
 * @param {object} [args.ndre] classifyLegend of the NDRE answer
 * @param {string} [args.date] the NDVI picture's date
 * @param {Array}  [args.patches] sectorPatches of the NDVI picture
 */
export const buildGrowthSignal = ({ ndvi, ndre = null, date = null, patches = [] } = {}) => {
  const ndviTone = rateClassified(ndvi);
  const ndreTone = ndre ? rateClassified(ndre) : 'unknown';
  const ndreCapped = ndreTone === 'bad' ? 'watch' : ndreTone;
  const tone = isTone(ndviTone) ? worseOf(ndviTone, ndreCapped) : ndviTone;
  const leafFlagged = isTone(ndviTone) && isTone(ndreCapped) && TONE_ORDER[ndreCapped] > TONE_ORDER[ndviTone];

  const base = {
    id: 'growth',
    icon: 'leaf',
    titleKey: 'sigGrowthTitle',
    tone,
    statusKey: statusKeyFor(tone),
    date,
    source: text(ndre && isTone(ndreTone) ? 'sigGrowthSourceBoth' : 'sigGrowthSource', { date }),
    drivenBy: leafFlagged ? 'ndre' : 'ndvi',
  };

  if (!isTone(tone)) {
    return { ...base, plain: unreadable(tone, date), detail: null, todo: null, patches: [] };
  }

  const flagged = flaggedPatches(patches);
  const weakPct = pct(ndvi.shares.bad + ndvi.shares.watch);
  const palePct = ndre && isTone(ndreTone) ? pct(ndre.shares.bad + ndre.shares.watch) : 0;
  const lead = flagged[0];
  const placeKey = lead ? lead.placeKey : 'placeWholeField';

  let plain;
  if (tone === 'good') {
    plain = lead ? text('growthGoodExcept', { placeKey }) : text('growthEven');
  } else if (flagged.length) {
    plain = parts(flagged.slice(0, 2).map((p) => text('growthPlaceWeak', { placeKey: p.placeKey })));
  } else if (leafFlagged) {
    plain = text('growthLeafPale', { pct: palePct });
  } else {
    plain = text('growthWeakShare', { pct: weakPct });
  }

  const detail = parts([
    text('growthDetail', { weakPct, goodPct: pct(ndvi.shares.good) }),
    ndre && isTone(ndreTone) && palePct > 0 ? text('growthDetailLeaf', { pct: palePct }) : null,
  ]);

  let todo;
  if (tone === 'good') todo = lead ? text('todoGrowthGoodLook', { placeKey }) : text('todoGrowthGood');
  else if (leafFlagged && !flagged.length) todo = text('todoGrowthLeaf');
  else if (tone === 'bad') todo = text('todoGrowthWeak', { placeKey });
  else todo = text('todoGrowthWatch', { placeKey });

  return { ...base, plain, detail, todo, patches: flagged };
};

/* ------------------------------------------------------- more layers ---- */

/**
 * Which of the two families a picture belongs to. NDRE is read like growth
 * (pale or weak plants), NDWI like water (dry or waterlogged); the to-dos and
 * the map card's class line follow the family, so a patch on either picture
 * is advised on in the same words as its sibling.
 */
const FAMILY = { water: 'water', growth: 'growth', ndre: 'growth', ndwi: 'water' };
export const familyOf = (signal) => FAMILY[signal] || 'water';

/**
 * Water stress from NDWI: the same reading as NDMI's — CropGen's NDWI classes
 * are toned like NDMI's (satelliteRules) — under its own name and source.
 * It is a layer of its own and does not feed the verdict or the crop guide.
 */
export const buildNdwiSignal = ({ classified, date = null, patches = [] } = {}) => ({
  ...buildWaterSignal({ classified, date, patches }),
  id: 'ndwi',
  titleKey: 'sigNdwiTitle',
  source: text('sigNdwiSource', { date }),
});

/**
 * Leaf colour from NDRE, shown on its own. As on the growth card, NDRE can
 * ask for a look but never on its own for action: a bad reading is WATCH,
 * here and on each of its patches.
 */
export const buildNdreSignal = ({ classified, date = null, patches = [] } = {}) => {
  const raw = rateClassified(classified);
  const tone = raw === 'bad' ? 'watch' : raw;
  const base = {
    id: 'ndre',
    icon: 'leaf',
    titleKey: 'sigNdreTitle',
    tone,
    statusKey: statusKeyFor(tone),
    date,
    source: text('sigNdreSource', { date }),
  };
  if (!isTone(tone)) return { ...base, plain: unreadable(tone, date), detail: null, todo: null, patches: [] };

  const flagged = flaggedPatches(patches).map((p) => (p.tone === 'bad' ? { ...p, tone: 'watch' } : p));
  const palePct = pct(classified.shares.bad + classified.shares.watch);
  const lead = flagged[0];
  let plain;
  if (tone === 'good') plain = lead ? text('ndreGoodExcept', { placeKey: lead.placeKey }) : text('ndreEven');
  else if (flagged.length) plain = parts(flagged.slice(0, 2).map((p) => text('ndrePlacePale', { placeKey: p.placeKey })));
  else plain = text('growthLeafPale', { pct: palePct });

  return {
    ...base,
    plain,
    detail: text('ndreDetail', { pct: palePct }),
    todo: tone === 'good' ? text('todoGrowthGood') : text('todoGrowthLeaf'),
    patches: flagged,
  };
};

/* ------------------------------------------------------------ verdict ---- */

/**
 * The banner above the cards: how many things need attention.
 *
 * Signals that could not be read are neither counted nor called normal. If
 * none could be read, the verdict says so rather than "all looks normal".
 */
export const buildVerdict = (signals = []) => {
  const readable = signals.filter((s) => s && isTone(s.tone));
  if (!readable.length) {
    return { tone: 'unknown', titleKey: 'verdictUnknown', count: 0, todos: [] };
  }
  const flagged = readable.filter((s) => s.tone !== 'good');
  if (!flagged.length) {
    return {
      tone: 'good',
      titleKey: 'verdictAllNormal',
      count: 0,
      todos: readable.map((s) => s.todo).filter(Boolean).slice(0, 1),
    };
  }
  const tone = flagged.some((s) => s.tone === 'bad') ? 'bad' : 'watch';
  const ordered = [...flagged].sort((a, b) => TONE_ORDER[b.tone] - TONE_ORDER[a.tone]);
  return {
    tone,
    titleKey: flagged.length === 1 ? 'verdictOneThing' : 'verdictNThings',
    count: flagged.length,
    todos: ordered.map((s) => s.todo).filter(Boolean),
  };
};

/* ------------------------------------------------------------ patches ---- */

const PATCH_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];
const MORE_LETTERS = ['G', 'H', 'J', 'K', 'L', 'M'];

/**
 * The patches of both cards as one list, lettered once.
 *
 * Each picture letters its own patches from A, so the water card's A and the
 * growth card's A are different places. The patch list and the map markers
 * need one set of letters: worst first, water before growth on a tie, then
 * larger before smaller.
 *
 * @returns {Array} patches with `signal` ('water' | 'growth') and a new `letter`
 */
export const fieldPatches = (signals = []) =>
  signals
    .filter(Boolean)
    .flatMap((signal) => (signal.patches || []).map((patch) => ({ ...patch, signal: signal.id })))
    .sort(
      (a, b) =>
        TONE_ORDER[b.tone] - TONE_ORDER[a.tone] ||
        (a.signal === b.signal ? 0 : a.signal === 'water' ? -1 : 1) ||
        b.sharePct - a.sharePct
    )
    .slice(0, PATCH_LETTERS.length)
    .map((patch, i) => ({ ...patch, key: `${patch.signal}-${patch.key}`, letter: PATCH_LETTERS[i] }));

/**
 * The patches of further pictures (NDRE, NDWI), lettered after `lettered`.
 *
 * fieldPatches letters water and growth for every farm screen and the crop
 * guide; another picture must not re-letter those, so its patches carry on
 * from the next free letter, ordered the same way.
 *
 * @param {Array} lettered fieldPatches of water and growth
 * @param {Array} signals further signals, each with `id` and `patches`
 */
export const morePatches = (lettered = [], signals = []) => {
  const used = new Set(lettered.map((p) => p.letter));
  const free = [...PATCH_LETTERS, ...MORE_LETTERS].filter((l) => !used.has(l));
  return signals
    .filter(Boolean)
    .flatMap((signal) => (signal.patches || []).map((patch) => ({ ...patch, signal: signal.id })))
    .sort((a, b) => TONE_ORDER[b.tone] - TONE_ORDER[a.tone] || b.sharePct - a.sharePct)
    .slice(0, free.length)
    .map((patch, i) => ({ ...patch, key: `${patch.signal}-${patch.key}`, letter: free[i] }));
};

/* --------------------------------------------------------------- taps ---- */

/** The to-do for one place, from what the satellite saw there. */
const placeTodo = (signal, tone, kind, placeKey) => {
  const params = { placeKey };
  if (familyOf(signal) === 'growth') return text(tone === 'bad' ? 'todoGrowthWeak' : 'todoGrowthWatch', params);
  if (kind === 'wet') return text('todoWaterWet', params);
  return text(tone === 'bad' ? 'todoWaterDry' : 'todoWaterWatchDry', params);
};

/**
 * What the map card says about one tapped patch: its status, CropGen's class
 * there, and what to do about that place in particular.
 *
 * @param {object} patch one of fieldPatches
 * @returns {{ tone, statusKey, topClass, todo } | null}
 */
export const patchAdvice = (patch) => {
  if (!patch) return null;
  return {
    tone: patch.tone,
    statusKey: statusKeyFor(patch.tone),
    topClass: patch.topClass,
    todo: patch.tone === 'good' ? text('mapSpotFine') : placeTodo(patch.signal, patch.tone, patch.kind, patch.placeKey),
  };
};

/**
 * What the map card says about a single tapped spot outside every patch.
 *
 * @param {{ label, tone, kind }} cls the class at that spot (classAt)
 * @param {'water'|'growth'|'ndre'|'ndwi'} signal the picture being shown
 * @returns {{ tone, statusKey, topClass, todo }}
 */
export const spotAdvice = (cls, signal) => {
  if (!cls) return null;
  if (cls.tone === 'cloud') {
    return { tone: 'cloudy', statusKey: statusKeyFor('cloudy'), topClass: cls.label, todo: text('mapSpotCloud') };
  }
  if (!isTone(cls.tone)) {
    return { tone: 'unknown', statusKey: statusKeyFor('unknown'), topClass: cls.label, todo: null };
  }
  // Leaf colour alone asks for a look, never for action (buildNdreSignal).
  const tone = signal === 'ndre' && cls.tone === 'bad' ? 'watch' : cls.tone;
  return {
    tone,
    statusKey: statusKeyFor(tone),
    topClass: cls.label,
    todo: tone === 'good' ? text('mapSpotFine') : placeTodo(signal, tone, cls.kind, 'placeThisSpot'),
  };
};

/* ------------------------------------------------------------ seasons ---- */

const finite = (n) => typeof n === 'number' && Number.isFinite(n);

/** The season's mean NDVI and how many dated readings it rests on. */
const seasonMean = (data) => {
  const values = (data && Array.isArray(data.entries) ? data.entries : []).map((e) => e.mean).filter(finite);
  const summaryMean = data && data.summary ? Number(data.summary.mean) : NaN;
  const mean = Number.isFinite(summaryMean)
    ? summaryMean
    : values.length
    ? values.reduce((a, b) => a + b, 0) / values.length
    : null;
  return { mean, readings: values.length };
};

/** The tone most of a season's readings carry, in CropGen's own words. */
const seasonTone = (data, index) => {
  const count = { good: 0, watch: 0, bad: 0 };
  (data && Array.isArray(data.entries) ? data.entries : []).forEach((entry) => {
    const tone = toneForRating(index, entry.rating);
    if (isTone(tone)) count[tone] += 1;
  });
  const total = count.good + count.watch + count.bad;
  if (!total) return 'unknown';
  return Object.keys(count).reduce((a, b) =>
    count[b] > count[a] || (count[b] === count[a] && TONE_ORDER[b] > TONE_ORDER[a]) ? b : a
  );
};

const median = (values) => {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

/** Bars never shrink to nothing: a weak season still has to be seen. */
const MIN_BAR_PCT = 8;

/**
 * "Season by season": one bar per year for the same months, and what they say.
 *
 * The value on a bar is the season's mean NDVI × 100 — greenness on a 0–100
 * scale, not a soil score. Its colour is the tone of CropGen's own rating of
 * most of that season's readings, sorted by the same rules as today's legend.
 * A season with too few readings is shown as missing and left out of the trend
 * rather than scored on one pass that may have been cloud.
 *
 * @param {Array<{year: number, isNow: boolean, status: 'loading'|'ready'|'error', data: object|null}>} seasons
 *   oldest first; `data` is a normaliseIndexHistory result
 * @param {object} [options]
 * @param {string} [options.index] the index charted
 * @returns {{ bars: Array, trend: 'up'|'down'|'flat'|'unknown', headline, sub, loading: boolean, readable: number }}
 */
export const buildSeasonHistory = (seasons = [], { index = 'ndvi' } = {}) => {
  const { flatDelta, minReadings } = SEASON_HISTORY;

  const bars = seasons.map((season) => {
    const base = { year: season.year, isNow: Boolean(season.isNow) };
    if (season.status === 'loading') return { ...base, state: 'loading', value: null, mean: null, tone: 'unknown' };
    const data = season.status === 'ready' ? season.data : null;
    const ok = data && data.status === 'OK';
    const { mean, readings } = ok ? seasonMean(data) : { mean: null, readings: 0 };
    if (!ok || mean == null || readings < minReadings) {
      return { ...base, state: 'missing', value: null, mean: null, tone: 'unknown', readings };
    }
    return {
      ...base,
      state: 'ready',
      mean,
      value: Math.round(mean * 100),
      tone: seasonTone(data, index),
      readings,
    };
  });

  const scored = bars.filter((b) => b.state === 'ready');
  const top = scored.reduce((max, b) => Math.max(max, b.value), 0);
  bars.forEach((bar) => {
    // eslint-disable-next-line no-param-reassign
    bar.heightPct = bar.state === 'ready' && top > 0 ? Math.max(MIN_BAR_PCT, Math.round((bar.value / top) * 100)) : MIN_BAR_PCT;
  });

  const now = scored.find((b) => b.isNow);
  const past = scored.filter((b) => !b.isNow);
  const loading = bars.some((b) => b.state === 'loading');

  let trend = 'unknown';
  if (now && past.length) {
    const delta = now.mean - median(past.map((b) => b.mean));
    trend = delta > flatDelta ? 'up' : delta < -flatDelta ? 'down' : 'flat';
  }

  let headline;
  if (trend === 'up') headline = text('seasonTrendUp');
  else if (trend === 'down') headline = text('seasonTrendDown');
  else if (trend === 'flat') headline = text(now.tone === 'good' ? 'seasonTrendFlatGood' : 'seasonTrendFlat');
  else headline = text('seasonTrendUnknown');

  const weak = past.filter((b) => b.tone === 'bad' || b.tone === 'watch');
  const weakest = weak.length ? weak.reduce((a, b) => (b.mean < a.mean ? b : a)) : null;
  const sub = parts([
    scored.length ? text('seasonSubN', { n: scored.length }) : text('seasonSubNone'),
    weakest ? text('seasonWeakest', { year: weakest.year }) : null,
  ]);

  return { bars, trend, headline, sub, loading, readable: scored.length };
};

/* -------- layers (farmer-app src/config/stageWatch.js) -------- */

/**
 * The layers My farm can draw on the map. `id` is the picture's id on the
 * map and in the patches (water and growth keep the names every other farm
 * screen uses); `index` is the backend's.
 */
export const MAP_LAYERS = {
  growth: { id: 'growth', index: 'ndvi', labelKey: 'mfLayerGrowth', code: 'NDVI', icon: 'leaf', tile: 'green' },
  ndre: { id: 'ndre', index: 'ndre', labelKey: 'mfLayerNdre', code: 'NDRE', icon: 'seedling', tile: 'purple' },
  ndwi: { id: 'ndwi', index: 'ndwi', labelKey: 'mfLayerNdwi', code: 'NDWI', icon: 'water', tile: 'blue' },
  water: { id: 'water', index: 'ndmi', labelKey: 'mfLayerWater', code: 'NDMI', icon: 'water', tile: 'blue' },
};

/** Every layer, in the order shown when the stage does not choose. */
export const LAYER_ORDER = ['growth', 'ndre', 'water', 'ndwi'];

/* -------- findings (farmer-app src/services/myFarmModel.js) -------- */

/** The map's layers, the stage's own first and then the rest, each once. */
export const layersFor = (group) => {
  const lead = group ? group.layers : [];
  return [...lead, ...LAYER_ORDER.filter((id) => !lead.includes(id))];
};

const rank = (tone) => (isTone(tone) ? TONE_ORDER[tone] : -1);

/**
 * "What the satellite found": for each layer in the stage's order, its named
 * patches — or, with none, one line about the whole field. A layer whose
 * picture could not be read says so rather than being left out.
 *
 * @param {string[]} layerIds from layersFor
 * @param {Object<string, object>} signals layer id → signal (buildWaterSignal …)
 * @param {Array} patches every lettered patch (fieldPatches + morePatches)
 * @param {object} [options]
 * @param {number} [options.limit] at most this many rows
 * @returns {Array<{key, layer, tone, patch?, text}>}
 */
export const findingsFor = (layerIds, signals, patches = [], { limit = 4 } = {}) => {
  const rows = [];
  layerIds.forEach((layer) => {
    const signal = signals[layer];
    if (!signal) return;
    const own = patches.filter((p) => p.signal === layer && (p.tone === 'bad' || p.tone === 'watch'));
    if (own.length) {
      own.forEach((patch) => rows.push({ key: patch.key, layer, tone: patch.tone, patch, text: signal.plain, todo: signal.todo }));
    } else {
      rows.push({ key: `field-${layer}`, layer, tone: signal.tone, patch: null, text: signal.plain, todo: signal.todo });
    }
  });
  // Stage order is kept within a tone; worse first across tones, unread last.
  return rows
    .map((row, i) => ({ row, i }))
    .sort((a, b) => rank(b.row.tone) - rank(a.row.tone) || a.i - b.i)
    .slice(0, limit)
    .map(({ row }) => row);
};

/* -------- season windows (farmer-app src/hooks/useFarmSatellite.js) -------- */

const shiftDays = (day, delta) => {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + delta);
  return date.toISOString().slice(0, 10);
};

const shiftYears = (day, delta) => {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCFullYear(date.getUTCFullYear() + delta);
  return date.toISOString().slice(0, 10);
};

/** Today, as the backend counts days (the farm's calendar, not the phone's). */
export const seasonToday = (now = new Date(), timeZone = SEASON_HISTORY.timeZone) =>
  new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);

/**
 * The windows "Season by season" asks for, oldest first.
 *
 * The current season is the plain last-`windowDays` request — the same
 * question Farm Detail and the pre-fetch already ask — so it is usually in
 * hand. Each past season is the same stretch of the calendar k years back.
 *
 * @returns {Array<{year: number, isNow: boolean, start: string|null, end: string|null}>}
 */
export const seasonWindows = (today, { years = SEASON_HISTORY.years, windowDays = SEASON_HISTORY.windowDays } = {}) => {
  const out = [];
  for (let k = years; k >= 1; k -= 1) {
    const end = shiftYears(today, -k);
    out.push({ year: Number(end.slice(0, 4)), isNow: false, start: shiftDays(end, -windowDays), end });
  }
  out.push({ year: Number(today.slice(0, 4)), isNow: true, start: null, end: null });
  return out;
};

/* -------- farm boundary (farmer-app src/services/farmModel.js) -------- */

export const AREA_UNITS = {
  'sq mtr': 1,
  Acres: 4046.8564,
  Hectare: 10000,
  Bigha: 2529.28,
  Gaj: 0.836127,
  Kanal: 505.857,
  Biswa: 126.464,
  Dismil: 40.468,
  Killa: 4046.8564,
  Marla: 25.2928,
  Nali: 200,
  Guntha: 101.171,
  'Square Karam': 2.529,
  Ankanam: 6.689,
  Gajam: 0.836127,
  Chatak: 4.18,
  Dhur: 6.323,
  Perch: 25.2928,
  Lessa: 14.4,
  Murabba: 101171.41,
  'Biswa Kacha': 42.15,
  Pura: 4000,
};

export const m2ToUnit = (m2, unit) => (Number(m2) || 0) / (AREA_UNITS[unit] || 1);
export const unitToM2 = (value, unit) => (Number(value) || 0) * (AREA_UNITS[unit] || 1);


/**
 * Farm boundaries reach us in several shapes depending on which portal wrote
 * them: {latitude, longitude}, {lat, lng}, or a raw [lng, lat] pair. Normalise
 * everything to {lat, lng} and drop anything that is not a finite coordinate.
 */
export const normaliseRing = (raw) => {
  const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
  return list
    .map((p) => {
      if (Array.isArray(p) && p.length >= 2) {
        // A bare pair is read as GeoJSON order, [lng, lat] — that is what the
        // spec says and what every writer on this platform emits. The pair
        // [79.1, 27.1] is genuinely ambiguous on its own (it is a valid
        // latitude either way), so guessing from magnitude alone would silently
        // move farms across the planet. We only accept the reversed order when
        // the GeoJSON reading is impossible: a latitude cannot exceed 90.
        const [first, second] = p.map(Number);
        if (Math.abs(second) > 90 && Math.abs(first) <= 90) return { lat: first, lng: second };
        return { lat: second, lng: first };
      }
      if (p && typeof p === 'object') {
        const lat = Number(p.lat ?? p.latitude ?? p.Lat ?? p.y);
        const lng = Number(p.lng ?? p.lon ?? p.longitude ?? p.Lng ?? p.x);
        return { lat, lng };
      }
      return null;
    })
    .filter((p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng) && Math.abs(p.lat) <= 90 && Math.abs(p.lng) <= 180);
};

/* -------- geometry (farmer-app src/geotag/geometry.js) -------- */

const toRad = Math.PI / 180;

/** Geodesic area of a {lat,lng} ring, in m². Ring may be open or closed. */
export const polygonAreaM2 = (ring) => {
  if (!Array.isArray(ring) || ring.length < 3) return 0;
  const coords = ring.map((p) => [p.lng, p.lat]);
  const first = coords[0];
  const last = coords[coords.length - 1];
  if (first[0] !== last[0] || first[1] !== last[1]) coords.push(first);
  try {
    return Math.abs(area(turfPolygon([coords])));
  } catch {
    return 0;
  }
};

export const centroidOf = (ring) => {
  if (!ring?.length) return null;
  // Area-weighted centroid in a local planar frame — a plain vertex mean drifts
  // toward whichever edge happens to carry more vertices.
  const lat0 = ring.reduce((s, p) => s + p.lat, 0) / ring.length;
  const kx = Math.cos(lat0 * toRad);
  let twiceArea = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < ring.length; i += 1) {
    const p = ring[i];
    const q = ring[(i + 1) % ring.length];
    const x1 = p.lng * kx;
    const y1 = p.lat;
    const x2 = q.lng * kx;
    const y2 = q.lat;
    const cross = x1 * y2 - x2 * y1;
    twiceArea += cross;
    cx += (x1 + x2) * cross;
    cy += (y1 + y2) * cross;
  }
  if (Math.abs(twiceArea) < 1e-12) {
    return {
      lat: lat0,
      lng: ring.reduce((s, p) => s + p.lng, 0) / ring.length,
    };
  }
  return { lat: cy / (3 * twiceArea), lng: cx / (3 * twiceArea) / kx };
};

export const boundsOf = (ring) => {
  if (!ring?.length) return null;
  let north = -90;
  let south = 90;
  let east = -180;
  let west = 180;
  ring.forEach((p) => {
    if (p.lat > north) north = p.lat;
    if (p.lat < south) south = p.lat;
    if (p.lng > east) east = p.lng;
    if (p.lng < west) west = p.lng;
  });
  return { north, south, east, west };
};

/* -------- the Support page's farm -------- */

/**
 * A farm row from /super-admin/farms as the Support page uses it: the
 * boundary normalised the way farmer-app's normaliseFarm does it, and its
 * area, centre and bounds. A row with fewer than three usable points has no
 * boundary, and nothing is asked of the satellite for it.
 */
export const farmFromRow = (row) => {
  if (!row) return null;
  const ring = normaliseRing(row.geo_location?.data ?? row.geo_location ?? row.geoFence ?? row.boundary);
  const storedUnit = row.farm_size_unit || 'Acres';
  const storedSize = Number(row.farm_size);
  const computedM2 = ring.length >= 3 ? polygonAreaM2(ring) : 0;
  const areaM2 = computedM2 > 0 ? computedM2 : Number.isFinite(storedSize) ? unitToM2(storedSize, storedUnit) : 0;
  return {
    id: row.farm_id ?? row.id ?? null,
    name: row.farm_name || row.name || 'Farm',
    ownerName: row.owned_by_name || null,
    ring,
    hasBoundary: ring.length >= 3,
    acres: m2ToUnit(areaM2, 'Acres'),
    centre: ring.length ? centroidOf(ring) : null,
    bounds: ring.length ? boundsOf(ring) : null,
  };
};

/** Whether a farm row has a boundary the satellite can be asked about. */
export const hasUsableBoundary = (row) =>
  normaliseRing(row?.geo_location?.data ?? row?.geo_location ?? row?.geoFence ?? row?.boundary).length >= 3;
